import { useEffect, useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../utils/firebase';

/**
 * useBeerDuplicateReviews
 * Fetches beer duplicate review documents via listDuplicateReviews callable,
 * filtered by breweryId and optional status (pending | reviewed).
 * When globalScope is true, fetches all reviews regardless of brewery.
 */
function useBeerDuplicateReviews(breweryId, status = 'pending', options = {}) {
  const { globalScope = false } = options;
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    if (!globalScope && !breweryId) {
      setReviews([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const listFn = httpsCallable(
        functions,
        'callableBeerListDuplicateReviews-listDuplicateReviews',
      );
      const requestPayload = globalScope
        ? { status }
        : { breweryId, status };
      const result = await listFn(requestPayload);
      const data = result.data ?? {};
      const rawReviews = Array.isArray(data.reviews) ? data.reviews : [];

      const rows = rawReviews.map((item) => ({
        ...item,
        review_outcome: item.reviewOutcome ?? item.review_outcome,
      }));

      setReviews(rows);
    } catch (err) {
      console.error('Error fetching beer duplicate reviews:', err);
      setError('Failed to load beer duplicate reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, [breweryId, status, globalScope]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { reviews, loading, error, refresh };
}

export default useBeerDuplicateReviews;
